// src/components/HealthImpact.jsx

import React from "react";
import { HeartPulse, Brain, Baby, Wind } from "lucide-react";

export const HealthImpact = () => {
  return (
    <section
      id="health-impact"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto space-y-20">

        {/* --------------------- Header --------------------- */}
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-[var(--aerzen-black)] mb-6">
            Pollution Doesn’t Stop at Your Door —
            <span className="text-[var(--aerzen-blue)]"> It Follows You Inside</span>
          </h2>

          <p className="text-lg sm:text-xl text-[var(--aerzen-black)]/70 leading-relaxed max-w-2xl mx-auto">
            PM2.5 is 30× thinner than a strand of hair.
            It enters from gaps in windows, doors and ventilation — making indoor air
            <strong> 3–5× more polluted</strong> during winter, Diwali, and high-traffic days.
          </p>
        </div>

        {/* --------------------- Impact Grid --------------------- */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Lungs (Replaced with Wind icon) */}
          <div className="bg-[var(--aerzen-blue)]/5 p-8 rounded-2xl shadow-sm hover:shadow-lg transition">
            <Wind className="h-10 w-10 text-[var(--aerzen-blue)] mb-4" />
            <h3 className="font-bold text-xl mb-2">Lung Damage</h3>
            <p className="text-[var(--aerzen-black)]/60">
              Constant exposure reduces lung strength over time
              and irritates airways — especially in polluted cities.
            </p>
          </div>

          {/* Heart */}
          <div className="bg-[var(--aerzen-blue)]/5 p-8 rounded-2xl shadow-sm hover:shadow-lg transition">
            <HeartPulse className="h-10 w-10 text-[var(--aerzen-blue)] mb-4" />
            <h3 className="font-bold text-xl mb-2">Heart Stress</h3>
            <p className="text-[var(--aerzen-black)]/60">
              PM2.5 spikes are linked with increased heart strain,
              breathlessness, and higher blood pressure episodes.
            </p>
          </div>

          {/* Brain */}
          <div className="bg-[var(--aerzen-blue)]/5 p-8 rounded-2xl shadow-sm hover:shadow-lg transition">
            <Brain className="h-10 w-10 text-[var(--aerzen-blue)] mb-4" />
            <h3 className="font-bold text-xl mb-2">Cognitive Decline</h3>
            <p className="text-[var(--aerzen-black)]/60">
              Polluted indoor air affects sleep cycles, concentration,
              and daily productivity — proven in multiple studies.
            </p>
          </div>

          {/* Babies */}
          <div className="bg-[var(--aerzen-blue)]/5 p-8 rounded-2xl shadow-sm hover:shadow-lg transition">
            <Baby className="h-10 w-10 text-[var(--aerzen-blue)] mb-4" />
            <h3 className="font-bold text-xl mb-2">Infants Are Most Vulnerable</h3>
            <p className="text-[var(--aerzen-black)]/60">
              Babies breathe 2× faster and absorb more pollutants.
              Their lungs and immunity are still developing.
            </p>
          </div>
        </div>

        {/* --------------------- Doctor Line --------------------- */}
        <div className="bg-[var(--aerzen-blue)]/10 rounded-3xl p-12 shadow-xl">
          <h3 className="text-3xl font-bold text-[var(--aerzen-black)] text-center mb-8">
            What Health Experts Recommend
          </h3>

          <p className="text-lg text-[var(--aerzen-black)]/70 leading-relaxed max-w-4xl mx-auto text-center">
            Respiratory specialists strongly advise families to
            <strong> limit indoor PM2.5 exposure</strong>, especially during winter and festive periods
            when pollution levels rise sharply.
            Clean indoor air is crucial for infants, elders, and asthma-sensitive individuals.
          </p>
        </div>

        {/* --------------------- Transition Line --------------------- */}
        <p className="text-center text-xl text-[var(--aerzen-black)]/70 max-w-3xl mx-auto">
          This is why AERZEN AIR™ focuses not just on filtration —
          but on protecting your family when India’s air is at its worst.
        </p>

        {/* --------------------- CTA --------------------- */}
        <div className="text-center">
          <a
            href="#diwali-protection"
            className="inline-block bg-[var(--aerzen-blue)] text-white px-10 py-4 rounded-xl shadow-lg hover:bg-[#2d5bc4] text-lg font-semibold transition"
          >
            See How AERZEN AIR™ Protects Your Home →
          </a>
        </div>

      </div>
    </section>
  );
};

export default HealthImpact;
